import { readPublicConfigValue } from './runtimeConfig'

export interface ApiUrlResolutionOptions {
    configuredApiUrl?: string
    isProduction?: boolean
}

export const DEFAULT_API_BASE_URL = '/api'

const ABSOLUTE_HTTP_URL_PATTERN = /^https?:\/\//i
const LOOPBACK_HOSTNAME_PATTERN = /^127(?:\.\d{1,3}){3}$/
const INTERNAL_BROWSER_UNSAFE_HOSTS = new Set([
    'localhost',
    '0.0.0.0',
    '::1',
    '[::1]',
    'backend',
])

const trimTrailingSlash = (value: string): string => {
    if (value === '/') {
        return value
    }

    return value.replace(/\/+$/, '')
}

const normalizeRelativeApiBaseUrl = (value: string): string => {
    const normalizedValue = trimTrailingSlash(value.trim())

    if (!normalizedValue) {
        return DEFAULT_API_BASE_URL
    }

    return normalizedValue.startsWith('/')
        ? normalizedValue
        : `/${normalizedValue}`
}

const isAbsoluteHttpUrl = (value: string): boolean =>
    ABSOLUTE_HTTP_URL_PATTERN.test(value)

const isLoopbackOrInternalHostname = (hostname: string): boolean => {
    const normalizedHostname = hostname.trim().toLowerCase()

    return (
        INTERNAL_BROWSER_UNSAFE_HOSTS.has(normalizedHostname) ||
        LOOPBACK_HOSTNAME_PATTERN.test(normalizedHostname)
    )
}

const readConfiguredApiUrl = (configuredApiUrl?: string): string => {
    const normalizedConfiguredApiUrl = configuredApiUrl?.trim()

    if (normalizedConfiguredApiUrl) {
        return normalizedConfiguredApiUrl
    }

    const runtimeApiUrl = readPublicConfigValue('VITE_API_URL', {
        fallbackToBuildTimeOnEmpty: true,
    })

    if (runtimeApiUrl) {
        return runtimeApiUrl
    }

    return readPublicConfigValue('VITE_API_BASE_URL', {
        fallbackToBuildTimeOnEmpty: true,
    })
}

const readIsProduction = (isProduction?: boolean): boolean =>
    isProduction ?? import.meta.env.PROD

export const resolveApiBaseUrl = (
    options: ApiUrlResolutionOptions = {}
): string => {
    const configuredApiUrl = readConfiguredApiUrl(options.configuredApiUrl)
    const isProduction = readIsProduction(options.isProduction)

    if (!configuredApiUrl) {
        return DEFAULT_API_BASE_URL
    }

    if (isAbsoluteHttpUrl(configuredApiUrl)) {
        try {
            const parsedUrl = new URL(configuredApiUrl)

            // 生产环境中忽略浏览器无法访问的回环/容器内地址，统一走同源反代。
            if (
                isProduction &&
                isLoopbackOrInternalHostname(parsedUrl.hostname)
            ) {
                return DEFAULT_API_BASE_URL
            }

            return trimTrailingSlash(parsedUrl.toString())
        } catch {
            return DEFAULT_API_BASE_URL
        }
    }

    return normalizeRelativeApiBaseUrl(configuredApiUrl)
}

export const resolveApiOrigin = (
    options: ApiUrlResolutionOptions = {}
): string => {
    const apiBaseUrl = resolveApiBaseUrl(options)

    if (!isAbsoluteHttpUrl(apiBaseUrl)) {
        return ''
    }

    return new URL(apiBaseUrl).origin
}
